import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import { ConsoleNavItem } from '../siteNavLinks';
import MenuIcon from '../MenuIcon';
import BagIcon from '../BagIcon';
import SearchIcon from '../SearchIcon';

import css from './TopbarDesktop.module.css';

/**
 * Editorial nav (Figma 562:397). Always inline — no priority/"More" overflow.
 * Labels and destinations come from Console top-bar custom links.
 */
const SiteNav = ({ currentPage, customLinks = [] }) => {
  if (customLinks.length === 0) {
    return null;
  }
  return (
    <ul className={css.navList}>
      {customLinks.map((linkConfig, index) => (
        <ConsoleNavItem
          key={`${linkConfig.text}_${index}`}
          linkConfig={linkConfig}
          currentPage={currentPage}
          itemClassName={css.navItem}
          linkClassName={css.navLink}
          currentLinkClassName={css.navLinkCurrent}
        />
      ))}
    </ul>
  );
};

const SignupLink = () => (
  <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
    <FormattedMessage id="TopbarDesktop.signup" />
  </NamedLink>
);

const LoginLink = () => (
  <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
    <FormattedMessage id="TopbarDesktop.login" />
  </NamedLink>
);

const SearchLink = ({ intl }) => {
  return (
    <NamedLink id="search-link" name="SearchPage" className={css.searchLink}>
      <SearchIcon
        rootClassName={css.searchIcon}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.search' })}
      />
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.search" />
      </span>
    </NamedLink>
  );
};

const BagLink = ({ notificationCount, inboxTab, isAuthenticated, intl }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const name = isAuthenticated ? 'InboxPage' : 'LoginPage';
  const paramsMaybe = isAuthenticated ? { params: { tab: inboxTab } } : {};
  return (
    <NamedLink
      className={css.bagLink}
      name={name}
      {...paramsMaybe}
      title={intl.formatMessage({ id: 'TopbarDesktop.bag' })}
    >
      <BagIcon ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.bag' })} />
      {notificationDot}
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  intl,
  notificationCount,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true} contentPosition="left" useArrow={false}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        {/* {notificationCount > 0 ? <span className={css.notificationDot} /> : null} */}
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="NewListingPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
              name="NewListingPage"
            >
              <FormattedMessage id="TopbarDesktop.createListing" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <FormattedMessage id="TopbarDesktop.savedLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout.
 *
 * Three columns with the wordmark dead centre: the logo must stay centred on
 * the viewport, not on whatever is left over, so the outer columns are equal
 * `1fr` tracks rather than a single flex row.
 *
 * Right cluster (Figma 562:437): Search (icon + label) → bag → avatar.
 *
 * The burger uses a native <button> (not the shared Button) so it is never
 * disabled during hydration and always has a stable id for modal focus return.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {('default' | 'search')?} props.variant cream bar on SearchPage (Figma 562:44)
 * @param {boolean?} props.isMenuOpen
 * @param {Function?} props.onMenuClick
 * @param {string?} props.menuButtonId
 * @param {string?} props.menuId
 * @returns {JSX.Element}
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    variant = 'default',
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    showCreateListingsLink,
    inboxTab,
    customLinks,
    isMenuOpen = false,
    onMenuClick,
    menuButtonId = 'desktopMenuButton',
    menuId = 'TopbarMobileMenu',
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className, {
    [css.search]: variant === 'search',
  });

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      notificationCount={notificationCount}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const handleMenuClick = event => {
    event.preventDefault();
    if (typeof onMenuClick === 'function') {
      onMenuClick(event);
    }
  };

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={css.left}>
        <button
          type="button"
          id={menuButtonId}
          className={css.menuButton}
          onClick={handleMenuClick}
          aria-expanded={isMenuOpen}
          aria-controls={menuId}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })} />
        </button>
        <SiteNav currentPage={currentPage} customLinks={customLinks} />
      </div>

      <LinkedLogo
        id="logo-topbar-desktop"
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage(
          { id: 'TopbarDesktop.logo' },
          { marketplaceName: config?.marketplaceName }
        )}
        linkToExternalSite={config?.topbar?.logoLink}
      />

      <div className={css.right}>
        <SearchLink intl={intl} />
        <BagLink
          notificationCount={notificationCount}
          inboxTab={inboxTab}
          isAuthenticated={authenticatedOnClientSide}
          intl={intl}
        />
        {loginLinkMaybe}
        {signupLinkMaybe}
        {profileMenuMaybe}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
